import type { ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import type { ToolboxCapability } from "@ttc-spectree/toolbox-contract/roles";
import { useCan, useCanAnywhere } from "@/hooks/queries/use-toolbox-access";
import { groomingPath } from "@/lib/grooming/paths";
import { TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";

interface RequireToolboxCapabilityProps {
  /** The capability the page needs: on the route's `:project`, or on any project with `anywhere`. */
  capability: ToolboxCapability;
  /**
   * Check `capability` on any project instead of the route's `:project`
   * (pages without a `:project` segment, such as `/toolbox/roles`). An Admin
   * holds every capability everywhere.
   */
  anywhere?: boolean;
  /** Where to send someone without the capability; defaults to the route project's backlog. */
  redirectTo?: string;
  children: ReactNode;
}

function Loading() {
  return (
    <div className="flex min-h-[300px] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

/** The route-project check (the original behaviour). */
function RequireOnRouteProject({ capability, redirectTo, children }: RequireToolboxCapabilityProps) {
  const { project = "" } = useParams();
  const { allowed, isLoading } = useCan(project, capability);
  if (isLoading) return <Loading />;
  if (!allowed) return <Navigate to={redirectTo ?? groomingPath(project, "backlog")} replace />;
  return <>{children}</>;
}

/** The any-project check. */
function RequireAnywhere({ capability, redirectTo, children }: RequireToolboxCapabilityProps) {
  const { allowed, isLoading } = useCanAnywhere(capability);
  if (isLoading) return <Loading />;
  if (!allowed) return <Navigate to={redirectTo ?? TOOLBOX_HOME_PATH} replace />;
  return <>{children}</>;
}

/**
 * Renders `children` only when the caller holds `capability` (epic
 * PI-SPEC-SW-E283): on the route's `:project` by default, or on any project
 * with `anywhere`. Otherwise redirects: to `redirectTo` when given, else to
 * that project's backlog (or to `/toolbox` with `anywhere`). The SpecTree
 * API refuses the same actions, so this only spares the person a page whose
 * every action would fail.
 */
export function RequireToolboxCapability(props: RequireToolboxCapabilityProps) {
  return props.anywhere ? <RequireAnywhere {...props} /> : <RequireOnRouteProject {...props} />;
}
